import {
    Button, Col, Collapse, ConfigProvider, Menu, Modal, Row, Space, Tag, theme as AntdTheme, Typography,
} from 'antd';
import {
    useMemo, useState,
} from 'react';

import { DARK_THEME_HTML_BACKGROUND_COLOR } from '@/constants';
import { useUIStore } from '@/stores';
import { lightTheme } from '@/themes';
import { UnacknowledgedChange } from '@/types';
import { formatDate, stripHtml } from '@/utils';

type ScriptChangesModalProps = {
    open: boolean;
    changes: UnacknowledgedChange[];
    onConfirm: () => void;
    onCancel: () => void;
    loading?: boolean;
    entityLabel?: string;
};

type InstructionItem = {
    name: string;
    value: string;
};

// Мапінг назв полів на українські
const FIELD_LABELS: Record<string, string> = {
    title: 'Назва',
    description: 'Опис',
    comment: 'Коментар',
    regulation: 'Регламент',
    instructions: 'Розділи',
    cross_sells: 'Крос-сейли',
    main_image: 'Головне зображення',
    gallery: 'Галерея',
    product_price: 'Ціна',
    product_sku: 'Артикул',
    product_id: 'ID продукту',
    priority: 'Пріоритет',
    type: 'Тип',
    is_active: 'Активність скрипта',
};

const getFieldLabel = (field: string): string => {
    return FIELD_LABELS[field] || field;
};

const formatChangedAt = (value: string): string => {
    const parsedDate = new Date(value);

    if (Number.isNaN(parsedDate.getTime())) {
        return value;
    }

    return formatDate(value);
};

// Витягує текст з HTML

const formatFieldPlainText = (field: string, value: unknown): string => {
    if (value === null || value === undefined || value === '') {
        return '';
    }
    if (field === 'type') {
        if (value === 'cross-sale') return 'Крос-сейл';
        if (value === 'default') return 'Скрипт';
    }
    if (field === 'is_active') {
        const active = value === true || value === 1 || value === '1';
        return active ? 'Активний' : 'Неактивний';
    }
    if (typeof value === 'object') {
        try {
            return JSON.stringify(value, null, 2);
        } catch {
            return String(value);
        }
    }
    return stripHtml(String(value));
};

// Компонент для відображення змін у розділах скрипта
const InstructionsDiff = ({
    oldInstructions,
    newInstructions,
    isDark,
    contentBg,
    lightThemeToken,
}: {
    oldInstructions: InstructionItem[];
    newInstructions: InstructionItem[];
    isDark: boolean;
    contentBg: string;
    lightThemeToken: ReturnType<typeof AntdTheme.getDesignToken>;
}) => {
    const [selectedKey, setSelectedKey] = useState<string>(newInstructions[0]?.name || oldInstructions[0]?.name || '');

    // Знаходимо всі унікальні назви секцій
    const allSections = useMemo(() => {
        const names = new Set<string>();
        oldInstructions.forEach((item) => names.add(item.name));
        newInstructions.forEach((item) => names.add(item.name));
        return Array.from(names);
    }, [oldInstructions, newInstructions]);

    // Перевіряємо чи секція змінилась
    const getSectionStatus = (name: string): 'added' | 'removed' | 'changed' | 'unchanged' => {
        const oldItem = oldInstructions.find((i) => i.name === name);
        const newItem = newInstructions.find((i) => i.name === name);

        if (!oldItem && newItem) return 'added';
        if (oldItem && !newItem) return 'removed';
        if (oldItem && newItem && oldItem.value !== newItem.value) return 'changed';
        return 'unchanged';
    };

    const menuItems = allSections.map((name) => {
        const status = getSectionStatus(name);
        let color = 'default';
        if (status === 'added') color = 'success';
        if (status === 'removed') color = 'error';
        if (status === 'changed') color = 'warning';

        return {
            key: name,
            label: (
                <Space>
                    <span style={{ whiteSpace: 'nowrap' }}>{name}</span>
                    {status !== 'unchanged' && (
                        <Tag color={color} style={{ marginLeft: 4 }}>
                            {status === 'added' && 'Додано'}
                            {status === 'removed' && 'Видалено'}
                            {status === 'changed' && 'Змінено'}
                        </Tag>
                    )}
                </Space>
            ),
        };
    });

    const selectedOld = oldInstructions.find((i) => i.name === selectedKey);
    const selectedNew = newInstructions.find((i) => i.name === selectedKey);

    const oldText = selectedOld ? stripHtml(selectedOld.value) : '';
    const newText = selectedNew ? stripHtml(selectedNew.value) : '';
    const sectionBorderColor = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.06)';
    const menuMaxHeight = 400;

    return (
        <div style={{ display: 'flex', gap: 16, minHeight: 400 }}>
            <div
                style={{
                    width: 280,
                    flexShrink: 0,
                    maxHeight: menuMaxHeight,
                    overflowY: 'auto',
                    overflowX: 'auto',
                }}
            >
                <Menu
                    mode="vertical"
                    selectedKeys={[selectedKey]}
                    onClick={(e) => setSelectedKey(e.key)}
                    items={menuItems}
                    style={{
                        minWidth: 'max-content',
                        borderRadius: 8,
                    }}
                />
            </div>
            <div
                className="wswg-content"
                style={{
                    flex: 1,
                    overflow: 'auto',
                    padding: 16,
                    background: contentBg,
                    borderRadius: 8,
                    color: isDark ? lightThemeToken.colorText : undefined,
                }}
            >
                <Typography.Title level={5} style={{ marginTop: 0 }}>
                    {selectedKey}
                </Typography.Title>
                {oldText || newText ? (
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={12}>
                            <Typography.Text type="secondary" strong>Було</Typography.Text>
                            <div
                                style={{
                                    marginTop: 8,
                                    padding: 12,
                                    borderRadius: 8,
                                    border: `1px solid ${sectionBorderColor}`,
                                    whiteSpace: 'pre-wrap',
                                    lineHeight: 1.7,
                                    minHeight: 120,
                                    maxHeight: 360,
                                    overflow: 'auto',
                                }}
                            >
                                {oldText || '—'}
                            </div>
                        </Col>
                        <Col xs={24} md={12}>
                            <Typography.Text type="secondary" strong>Стало</Typography.Text>
                            <div
                                style={{
                                    marginTop: 8,
                                    padding: 12,
                                    borderRadius: 8,
                                    border: `1px solid ${sectionBorderColor}`,
                                    whiteSpace: 'pre-wrap',
                                    lineHeight: 1.7,
                                    minHeight: 120,
                                    maxHeight: 360,
                                    overflow: 'auto',
                                }}
                            >
                                {newText || '—'}
                            </div>
                        </Col>
                    </Row>
                ) : (
                    <Typography.Text type="secondary">Немає контенту</Typography.Text>
                )}
            </div>
        </div>
    );
};

// Порівняння простих полів: дві колонки «Було» / «Стало»
const SimpleFieldDiff = ({
    field,
    oldValue,
    newValue,
    isDark,
    contentBg,
}: {
    field: string;
    oldValue: unknown;
    newValue: unknown;
    isDark: boolean;
    contentBg: string;
}) => {
    const oldText = formatFieldPlainText(field, oldValue);
    const newText = formatFieldPlainText(field, newValue);

    if (!oldText && !newText) {
        return <Typography.Text type="secondary">—</Typography.Text>;
    }

    const borderColor = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.06)';

    return (
        <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
                <Typography.Text type="secondary" strong>Було</Typography.Text>
                <div
                    style={{
                        marginTop: 8,
                        padding: 12,
                        borderRadius: 8,
                        border: `1px solid ${borderColor}`,
                        background: contentBg,
                        whiteSpace: 'pre-wrap',
                        lineHeight: 1.7,
                        minHeight: 80,
                        maxHeight: 280,
                        overflow: 'auto',
                        fontFamily: 'monospace',
                        fontSize: 13,
                    }}
                >
                    {oldText || '—'}
                </div>
            </Col>
            <Col xs={24} md={12}>
                <Typography.Text type="secondary" strong>Стало</Typography.Text>
                <div
                    style={{
                        marginTop: 8,
                        padding: 12,
                        borderRadius: 8,
                        border: `1px solid ${borderColor}`,
                        background: contentBg,
                        whiteSpace: 'pre-wrap',
                        lineHeight: 1.7,
                        minHeight: 80,
                        maxHeight: 280,
                        overflow: 'auto',
                        fontFamily: 'monospace',
                        fontSize: 13,
                    }}
                >
                    {newText || '—'}
                </div>
            </Col>
        </Row>
    );
};

export const ScriptChangesModal = ({
    open,
    changes,
    onConfirm,
    onCancel,
    loading = false,
    entityLabel = 'скрипті',
}: ScriptChangesModalProps) => {
    const { theme: currentTheme } = useUIStore();
    const isDark = currentTheme === 'dark';

    const contentBg = useMemo(() => {
        return isDark ? DARK_THEME_HTML_BACKGROUND_COLOR : '#FAFAFA';
    }, [isDark]);

    const lightThemeToken = useMemo(() => {
        return AntdTheme.getDesignToken({
            algorithm: AntdTheme.defaultAlgorithm,
            ...lightTheme,
        });
    }, []);

    const collapseContentBg = isDark ? DARK_THEME_HTML_BACKGROUND_COLOR : undefined;

    const processedChanges = useMemo(() => {
        if (!Array.isArray(changes)) return [];

        const result: Array<{
            id: string;
            changedAt: string;
            field: string;
            oldValue: any;
            newValue: any;
            isInstructions: boolean;
        }> = [];

        changes.forEach((change) => {
            if (!change || !Array.isArray(change.changes)) return;

            change.changes.forEach((item, index) => {
                if (!item) return;

                result.push({
                    id: `${change.id}-${index}`,
                    changedAt: change.changed_at,
                    field: item.field,
                    oldValue: item.old_value,
                    newValue: item.new_value,
                    isInstructions: item.field === 'instructions',
                });
            });
        });

        return result;
    }, [changes]);

    const collapseItems = processedChanges.map((item) => ({
        key: item.id,
        label: (
            <Space>
                <Typography.Text strong>{getFieldLabel(item.field)}</Typography.Text>
                <Typography.Text type="secondary">{formatChangedAt(item.changedAt)}</Typography.Text>
            </Space>
        ),
        children: item.isInstructions ? (
            <InstructionsDiff
                oldInstructions={Array.isArray(item.oldValue) ? item.oldValue : []}
                newInstructions={Array.isArray(item.newValue) ? item.newValue : []}
                isDark={isDark}
                contentBg={contentBg}
                lightThemeToken={lightThemeToken}
            />
        ) : (
            <ConfigProvider
                theme={{
                    algorithm: isDark ? AntdTheme.defaultAlgorithm : undefined,
                    token: isDark
                        ? {
                            colorText: lightThemeToken.colorText,
                            colorLink: lightThemeToken.colorLink,
                        }
                        : undefined,
                }}
            >
                <div
                    className="wswg-content"
                    style={{
                        padding: 16,
                        background: contentBg,
                        borderRadius: 8,
                        color: isDark ? lightThemeToken.colorText : undefined,
                    }}
                >
                    <SimpleFieldDiff
                        field={item.field}
                        oldValue={item.oldValue}
                        newValue={item.newValue}
                        isDark={isDark}
                        contentBg={contentBg}
                    />
                </div>
            </ConfigProvider>
        ),
    }));

    const confirmButton = (
        <Button type="primary" onClick={onConfirm} loading={loading}>
            Підтвердити ознайомлення
        </Button>
    );

    return (
        <Modal
            title={(
                <div style={{ paddingRight: 8 }}>
                    <Typography.Title level={4} style={{ margin: 0, marginBottom: 4 }}>
                        Ознайомлення зі змінами в {entityLabel}
                    </Typography.Title>
                    <Typography.Text type="secondary">
                        Перегляньте зміни нижче. Після ознайомлення натисніть «Підтвердити ознайомлення».
                    </Typography.Text>
                </div>
            )}
            open={open}
            onCancel={onCancel}
            footer={
                <Space>
                    <Button onClick={onCancel}>Закрити</Button>
                    {confirmButton}
                </Space>
            }
            width="90vw"
            style={{ maxWidth: 1400, top: 20 }}
            styles={{ body: { maxHeight: 'calc(100vh - 200px)', overflow: 'auto' } }}
        >
            {processedChanges.length > 0 ? (
                <ConfigProvider
                    theme={{
                        components: {
                            Collapse: {
                                contentBg: collapseContentBg,
                            },
                        },
                    }}
                >
                    <Collapse
                        accordion
                        defaultActiveKey={[processedChanges[0]?.id]}
                        items={collapseItems}
                    />
                </ConfigProvider>
            ) : (
                <Typography.Text type="secondary">Немає змін для відображення</Typography.Text>
            )}
        </Modal>
    );
};
